import * as React from 'react';

// Compositional components
import { SpaceProps } from 'styled-system';
import { MenuItem } from 'component/layoutComponents/topNav/topNav.types';

// Styled components
import StyledSideNav, { BaseStyleProps, ChevronWrapper, IconWrapper, StyledListItem, NavItem } from './SideNav.style';

// Context
import { SideNavContext, useNavContext } from './context';
import { Collapse } from '@mui/material';
import Tooltip from './Tooltip/Tooltip';
import TextIcon from './TextIcon/TextIcon';
import { TruncatedText } from 'component/MarketPlace/Typography/Typography.style';
import { KeyboardArrowDown } from '@mui/icons-material';

export interface INavItemProps
  extends SpaceProps,
    Omit<React.HTMLProps<HTMLDivElement>, keyof 'role'> {
  /**
   * Disables the menu item
   */
  disabled?: boolean;
  /**
   * Click event handler
   */
  onClick?: React.MouseEventHandler;
  /**
   * Should nav item be keyboard/mouse accessible
   */
  readonly control?: boolean;
  /**
   * Icon to render to the left of Item title
   */
  icon?: React.ReactNode | string;

  hasSubMenu?: boolean;
  openSubMenu?: boolean;
  subMenuItem: MenuItem;
  locale?: any
  level?: number;
  onExpand?: () => void;
}

export interface ISideNavComposition {
  /**
   * Renders a nav item -- should be used to render links
   */
  Item: React.FC<INavItemProps>;
  /**
   * Renders a collapsible component
   */
  SubNav: React.FC<ISubNavProps>;
}

export interface ISideNavProps extends BaseStyleProps {
  /**
   * Determines the Item `navKey` to set active on load
   */
  defaultActiveItem?: string;
  /**
   * Array of SubNav `navKey` to have expanded by default
   */
  expanded?: string[];
  /**
   * Expands all SubNav instances; overrides `expanded` array
   */
  allExpanded?: boolean;
  /**
   * Show the SideNav in icon only mode. When the value is set to true,
   * any expanded SubNav Items will be collapsed and hidden. When the value is
   * set to false, the existing state and any consequential changes of the
   * SubNav during the icon only mode will be applied when returning to the full
   * icon and label mode.
   */
  iconOnlyMode?: boolean;
  /**
   * Callback function triggered when a SubNav is expanded or collapsed
   */
  onOpenChange?: (navKey: string) => void;

  children?: React.ReactNode;
}

export interface ISubNavProps extends SpaceProps {
  item: MenuItem;
  level?: number;
  onClick?: (item: MenuItem) => void;
  locale?: any;
}

const SideNav: React.FC<ISideNavProps> & ISideNavComposition = ({
  children,
  defaultActiveItem = '',
  expanded,
  allExpanded,
  iconOnlyMode,
  onOpenChange,
  ...rest
}) => {
  const [activeItem, setActiveItem] = React.useState(defaultActiveItem);
  /**
   * Memoize the context to prevent unnecessary renders.
   */
  const memoizedContextValue = React.useMemo(
    () => ({
      expanded,
      allExpanded,
      iconOnlyMode,
      onOpenChange,
      activeItem,
      setActiveItem
    }),
    [
      activeItem,
      setActiveItem,
      expanded,
      allExpanded,
      iconOnlyMode,
      onOpenChange
    ]
  );

  return (
    <SideNavContext.Provider value={memoizedContextValue}>
      <StyledSideNav {...rest}>{children}</StyledSideNav>
    </SideNavContext.Provider>
  );
};

const Item: React.FC<INavItemProps> = ({
  control = true, // Control is defaulted when Item is not within a subnav
  disabled,
  children,
  onClick,
  icon,
  hasSubMenu,
  openSubMenu,
  subMenuItem,
  locale,
  level = 0,
  onExpand,
  ...rest
}) => {
  const { iconOnlyMode } = useNavContext();
  const shouldBeTabbable = !disabled && control;
  let title = subMenuItem?.name ;
  const withIcon = !!icon || iconOnlyMode;

  return (
    <NavItem
      tabIndex={shouldBeTabbable ? 0 : -1}
      onClick={(e: React.MouseEvent<HTMLElement>) => {
        if (disabled) {
          e.preventDefault();
          return;
        }
        if (typeof onClick !== 'undefined') {
          onClick(e);
        }
      }}
      disabled={disabled}
      expanded={openSubMenu}
      withIcon={withIcon}
      iconOnlyMode={iconOnlyMode}
      hasSubMenu={hasSubMenu}
      level={level}
      {...(rest as any)}
    >
      {withIcon && (
        <IconWrapper
          pl={iconOnlyMode ? 0 : 3}
          disabled={disabled}
        >
          <Tooltip title={title} disabled={!iconOnlyMode}>
            {icon || <TextIcon label={title} disabled={disabled} />}
          </Tooltip>
        </IconWrapper>
      )}
      {!iconOnlyMode && (
        <>
          <TruncatedText
            maxlines={1}
            variant={level > 0 ? "body1" : "h5"}
            color="common.white"
            >
            {title} {subMenuItem?.isComingSoon && `(${locale?.['comingSoon']})`}
          </TruncatedText>
          {hasSubMenu && (
            <ChevronWrapper expanded={openSubMenu} onClick={(e: any) => {
              e.stopPropagation();
              onExpand?.();
            }}>
              <KeyboardArrowDown />
            </ChevronWrapper>
          )}
        </>
      )}
    </NavItem>
  );
};

const SubNav: React.FC<ISubNavProps> = ({
  item,
  level = 0,
  onClick,
  locale,
  ...rest
}) => {
  const [expanded, setExpanded] = React.useState(false);

  const hasSubMenu = item.items && item.items.length > 0;

  return (
    <StyledListItem role="menuitem" {...rest}>
      <Item
        onClick={() => onClick?.(item)}
        onExpand={() => setExpanded(!expanded)}
        openSubMenu={expanded}
        hasSubMenu={hasSubMenu}
        subMenuItem={item}
        disabled={!item.enabled}
        level={level}
        locale={locale}
      />
      {hasSubMenu && (
        <Collapse in={expanded}>
          <SideNav>
            {item.items?.map((subItem) => (
              <SubNav key={subItem.key} item={subItem} onClick={onClick} level={level + 1} />
            ))}
          </SideNav>
        </Collapse>
      )}
    </StyledListItem>
  );
};


SideNav.Item = Item;
SideNav.SubNav = SubNav;

SideNav.Item.displayName = 'SideNav.Item';
SideNav.SubNav.displayName = 'SideNav.SubNav';

export default SideNav;
